import { css } from 'react-emotion';
import { withWizard } from '@cimpress-technology/ff-react-components/lib/Wizard';
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import StepTitle from '../../../common/StepTitle';
import { Radio, RadioGroup, Select, TextField } from '@cimpress/react-components';
import WizardNavigator from '../../../common/WizardNavigator';
import Button from '../../../common/Button';
import { evaluateCondition } from '../../../../utilities/conditions';
import { connect } from 'react-redux';
import { selectors as identitySelectors } from '../../../../reducers/identities';
import { createSelector } from 'reselect';
import { MERCHANTS } from '../../../../constants/transactorType';
import { DEFAULT_FLOW } from '../ContractCreatePage';
import { PRICING_ARCH_V2 } from '../../../../constants/priceArchs';
import { getPricingArchVersion } from '../../../../services/PricingArchInfo';

const partyHeaderStyle = css({
  fontSize: '20px',
  marginBottom: '35px',
  marginTop: '0',
  textAlign: 'center'
});

const gracePeriodHeaderStyle = css({
  fontSize: '20px',
  marginTop: '0',
  textAlign: 'center'
});

const descriptionStyle = css({
  fontSize: '15px',
  textAlign: 'center',
  marginRight: '250px',
  marginLeft: '250px',
  color: '#697582'
});

// NOTE: sometimes throws warning of cannot call setState on unmounted component
// later look into making our promises cancelable
class GetStartedStep extends PureComponent {
  static defaultProps = {};
  static propTypes = {
    flow: PropTypes.object.isRequired,
    resetProgress: PropTypes.func.isRequired,
    merchantOptions: PropTypes.arrayOf(PropTypes.object).isRequired
  };

  state = {
    merchantOptions: [],
    selectedMulti: null,
    isSellerMigratedToV2: false
  };

  componentDidMount() {
    if (this.props.authorizedFulfillerOptions.length === 1) {
      this.onChangeInitiator(this.props.authorizedFulfillerOptions[0]);
    }
  }

  onChangeInitiator = async newInitiator => {
    const { initiator, selectedSkus, productPricingTerm, shippingPricingTerm } = this.props.flow;
    let architectureVersion;

    if (newInitiator && newInitiator.accountId) {
      architectureVersion = await getPricingArchVersion(newInitiator.accountId);
    }

    if (architectureVersion && architectureVersion.toLowerCase() === PRICING_ARCH_V2.toLowerCase()) {
      this.setState({ isSellerMigratedToV2: true });
      this.props.resetProgress({ ...DEFAULT_FLOW, initiator: newInitiator, acceptors: this.props.flow.acceptors });
    } else {
      this.setState({ isSellerMigratedToV2: false });
    }

    const warnReset =
      !!initiator &&
      (Object.keys(selectedSkus).length ||
        Object.keys(productPricingTerm).length ||
        Object.keys(shippingPricingTerm).length);
    if (warnReset) {
      window.confirm(this.props.t('createContract:messages.onlyFulfillersAllowed')) &&
        this.props.resetProgress({ ...DEFAULT_FLOW, initiator: newInitiator, acceptors: this.props.flow.acceptors });
    } else {
      this.props.onChange({ initiator: newInitiator });
    }
  };

  onChangeAcceptors = selectedMulti => {
    this.props.onChange({ acceptors: selectedMulti });
  };

  onChangeshouldHaveGracePeriod = (option, value) => {
    this.props.onChange({ shouldHaveGracePeriod: value });
  };

  onChangegracePeriodApprovalDays = e => {
    this.props.onChange({ gracePeriodApprovalDays: e.target.value });
  };

  onNext = () => {
    const { wizard } = this.props;
    wizard.setStep(wizard.next);
  };

  render() {
    const { t, flow, merchantOptions, authorizedFulfillerOptions } = this.props;
    const { acceptors, initiator, shouldHaveGracePeriod, gracePeriodApprovalDays } = flow;

    const conditions = [
      {
        predicate: !!initiator,
        text: t('createContract:getStarted.missingSellerMessage')
      },
      {
        predicate: !this.state.isSellerMigratedToV2,
        text: t('createContract:getStarted.sellerInPricingArchV2MessageTooltip')
      },
      {
        predicate: acceptors && acceptors.length > 0,
        text: t('createContract:getStarted.missingBuyerMessage')
      },
      {
        predicate: !shouldHaveGracePeriod || (shouldHaveGracePeriod && gracePeriodApprovalDays > 0),
        text: t('createContract:getStarted.invalidGracePeriodSelection')
      }
    ];

    const [validCondition, invalidConditionReason] = evaluateCondition(conditions, false);

    return (
      <div>
        <StepTitle icon="start-contract-l" title={t('createContract:getStarted.header')} />
        <div className="step">
          <h4 className={partyHeaderStyle}>{t('createContract:getStarted.selectSeller')}</h4>
          <div style={{ maxWidth: '500px', margin: '0 auto', minHeight: '78px' }}>
            {authorizedFulfillerOptions.length === 1 ? (
              <div style={{ textAlign: 'center' }}>
                <h5>{authorizedFulfillerOptions[0].name}</h5>
              </div>
            ) : (
              <Select
                label={t('common:commonTerminology.seller')}
                style={{ marginBottom: '5px !important' }}
                className={this.state.isSellerMigratedToV2 ? 'Select-control-error' : null}
                value={initiator}
                options={authorizedFulfillerOptions}
                onChange={this.onChangeInitiator}
              />
            )}
            {this.state.isSellerMigratedToV2 ? (
              <div>
                <span className="help-block">{t('createContract:getStarted.sellerInPricingArchV2Message')}</span>
                <a
                  href={`${process.env.REACT_APP_PRICINGMANAGER_V2_URL}`}
                  className="help-block"
                  style={{ textDecoration: 'underline' }}>
                  Pricing Manager V2
                </a>
              </div>
            ) : null}
          </div>
        </div>
        <div className="step">
          <h4 className={partyHeaderStyle}>{t('createContract:getStarted.selectBuyers')}</h4>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <Select
              label={t('common:commonTerminology.buyer_oneOrMore')}
              value={acceptors}
              options={merchantOptions}
              onChange={this.onChangeAcceptors}
              multi
            />
          </div>
        </div>
        <div className="step">
          <h4 className={gracePeriodHeaderStyle}>{t('createContract:getStarted.addGracePeriod')}</h4>
          <div className={descriptionStyle}>
            <p>{t('createContract:getStarted.gracePeriodDescription')}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <RadioGroup
              name={t('common:commonTerminology.gracePeriod')}
              onChange={this.onChangeshouldHaveGracePeriod}
              inline
              valueSelected={shouldHaveGracePeriod}>
              <Radio label={t('common:commonWords.yesText')} value={true} />
              <Radio label={t('common:commonWords.noText')} value={false} />
            </RadioGroup>
          </div>
          {shouldHaveGracePeriod && (
            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
              <TextField
                label={t('createContract:getStarted.gracePeriodLabel')}
                type="number"
                min="1"
                required
                value={gracePeriodApprovalDays}
                onChange={this.onChangegracePeriodApprovalDays}
              />
            </div>
          )}
        </div>
        <WizardNavigator
          onNext={this.onNext}
          customNext={
            <Button
              className="btn btn-primary"
              disabled={!validCondition}
              disabledTooltipContents={invalidConditionReason.join('\n')}
              onClick={this.onNext}>
              {t('common:commonWords.next')}
            </Button>
          }
        />
      </div>
    );
  }
}

const getMerchantOptions = createSelector(identitySelectors.getMerchantsSortedByName, identities =>
  identities.map(identity => ({ ...identity, type: MERCHANTS, value: identity.id, label: identity.name }))
);

function mapStateToProps(state) {
  return {
    authorizedFulfillerOptions: identitySelectors.getAuthorizedFulfillerOptions(state),
    merchantOptions: getMerchantOptions(state)
  };
}

export default connect(mapStateToProps)(withWizard(withTranslation()(GetStartedStep)));
